import { Chip, Typography } from "../../../themeV2";
import PropTypes from "prop-types";
import * as React from "react";
import { Link } from "react-router-dom";
import { InfluencerEvent, SignType } from "../../../domain";
import { currencyFormatterNoDecimals } from "../../../util/formatCurrency";
import { formatDateRangeReverseTZ } from "../../../util/formatDateRange";
import { formatDateStrTimeReverseTZ } from "../../../util/formatSingleDate";
import { currencyFormatter } from "../../PromoCodeManager/utils";
import css from "./SearchResultCard.module.css";

const routeFor = (item) =>
  item.display.route?.path ? `/${item.display.route.path}` : undefined;

function HoverCard({ onClick, href, children }) {
  return (
    <Link className={css.resultLink} onClick={onClick} to={href}>
      {children}
      <span className={css.hoverArrow}>&rarr;</span>
    </Link>
  );
}

HoverCard.propTypes = {
  onClick: PropTypes.func,
  href: PropTypes.string,
  children: PropTypes.node,
};

const InfluencerCard = React.memo(function InfluencerCard({ item, onClick }) {
  const eventLabel =
    !item.payload.isLive && item.payload.event?.eventType
      ? formatEventLabel(item.payload.event)
      : null;

  return (
    <HoverCard onClick={onClick} href={routeFor(item)}>
      <img
        className={
          item.payload.isLive ? css.influencerAvatarLive : css.influencerAvatar
        }
        src={item.display.image.url}
        alt={item.display.title}
        width="40"
        height="40"
      />
      <div className={css.cardBody}>
        <div className={css.titleRow}>
          <Typography variant="title-md" as="span" truncate>
            {item.display.title}
          </Typography>
          {item.payload.isLive && (
            <span className={css.liveBadge}>
              <span className={css.liveDot} />
              LIVE
            </span>
          )}
        </div>
        <Typography variant="label-lg" color="variant" truncate>
          {item.display.subtitle}
        </Typography>
        {eventLabel && <Chip label={eventLabel} className={css.eventBadge} />}
      </div>
    </HoverCard>
  );
});

InfluencerCard.propTypes = {
  item: PropTypes.object.isRequired,
  onClick: PropTypes.func,
};

const ProductCard = React.memo(function ProductCard({ item, onClick }) {
  const fulfillers = item.payload.fulfillers ?? [];
  const visibleFulfillers = fulfillers.slice(0, 3);
  const extraCount = fulfillers.length - 3;

  return (
    <HoverCard onClick={onClick} href={routeFor(item)}>
      <img
        className={css.productImage}
        src={item.display.image.url}
        alt={item.display.title}
        width="48"
        height="48"
      />
      <div className={css.cardBody}>
        <Typography variant="title-md" as="span" truncate>
          {item.display.title}
        </Typography>
        <Typography variant="label-lg" color="variant" truncate>
          {item.payload.productType} &middot;{" "}
          {SignType.toLabel(item.payload.signedType)}
        </Typography>
        {fulfillers.length > 0 && (
          <div className={css.fulfillerRow}>
            <div className={css.fulfillerAvatarStack}>
              {visibleFulfillers.map((f) => (
                <img
                  className={css.fulfillerAvatar}
                  key={f.id}
                  src={f.avatarUrl}
                  alt={f.name}
                  width="20"
                  height="20"
                />
              ))}
            </div>
            <Typography variant="label-lg" color="variant" as="span" truncate>
              {fulfillers
                .slice(0, 2)
                .map((f) => f.name)
                .join(", ")}
              {extraCount > 0 && (
                <Typography as="span" variant="label-lg" color="primary">
                  {" "}
                  + {extraCount} more
                </Typography>
              )}
            </Typography>
          </div>
        )}
      </div>
      {item.payload.priceCents && (
        <Typography
          variant="label-lg"
          color="variant"
          as="span"
          className={css.price}
        >
          {Math.round(item.payload.priceCents / 100) ===
          item.payload.priceCents / 100
            ? currencyFormatterNoDecimals.format(item.payload.priceCents / 100)
            : currencyFormatter.format(item.payload.priceCents / 100)}
        </Typography>
      )}
    </HoverCard>
  );
});

ProductCard.propTypes = {
  item: PropTypes.object.isRequired,
  onClick: PropTypes.func,
};

const CharacterCard = React.memo(function CharacterCard({ item, onClick }) {
  return (
    <HoverCard onClick={onClick} href={routeFor(item)}>
      <img
        className={css.characterImage}
        src={item.display.image.url}
        alt={item.display.title}
        width="44"
        height="44"
      />
      <div className={css.cardBody}>
        <Typography variant="title-md" as="span" truncate>
          {item.display.title}
        </Typography>
        <Typography variant="label-lg" color="variant" truncate>
          {item.display.subtitle || "Character"}
        </Typography>
      </div>
    </HoverCard>
  );
});

CharacterCard.propTypes = {
  item: PropTypes.object.isRequired,
  onClick: PropTypes.func,
};

const FranchiseCard = React.memo(function FranchiseCard({ item, onClick }) {
  return (
    <HoverCard onClick={onClick} href={routeFor(item)}>
      <img
        className={css.franchiseImage}
        src={item.display.image.url}
        alt={item.display.title}
        width="56"
        height="36"
      />
      <div className={css.accentBorderGreen}>
        <Typography variant="title-md" as="span" truncate>
          {item.display.title}
        </Typography>
        <Typography variant="label-lg" color="variant" truncate>
          {item.display.subtitle || "Franchise"}
        </Typography>
      </div>
    </HoverCard>
  );
});

FranchiseCard.propTypes = {
  item: PropTypes.object.isRequired,
  onClick: PropTypes.func,
};

const CollectionCard = React.memo(function CollectionCard({ item, onClick }) {
  return (
    <HoverCard onClick={onClick} href={routeFor(item)}>
      <img
        className={css.franchiseImage}
        src={item.display.image.url}
        alt={item.display.title}
        width="56"
        height="36"
      />
      <div className={css.accentBorderPrimary}>
        <Typography variant="title-md" as="span" truncate>
          {item.display.title}
        </Typography>
        <Typography variant="label-lg" color="variant" truncate>
          {item.display.subtitle || "Collection"}
        </Typography>
      </div>
    </HoverCard>
  );
});

CollectionCard.propTypes = {
  item: PropTypes.object.isRequired,
  onClick: PropTypes.func,
};

/* ─── Helpers ─── */

function formatEventLabel(event) {
  if (event.nextEventTime && event.type === InfluencerEvent.TYPES.DAY_TIME) {
    return `${event.eventType} · ${formatDateStrTimeReverseTZ(
      event.nextEventTime,
    )}`;
  }
  if (event.eventStartDate && event.type === InfluencerEvent.TYPES.RANGE) {
    return `${event.eventType} · ${formatDateRangeReverseTZ(
      event.eventStartDate,
      event.eventEndDate,
    )}`;
  }
  return event.eventType;
}
export const cardForItem = (item) => {
  if (item.kind === "franchise") return FranchiseCard;
  if (item.kind === "character") return CharacterCard;
  if (item.kind === "influencer" && item.payload.isCollection) {
    return CollectionCard;
  }
  if (item.kind === "influencer") return InfluencerCard;
  return ProductCard;
};

export const cardByKind = {
  influencers: InfluencerCard,
  products: ProductCard,
  characters: CharacterCard,
  franchises: FranchiseCard,
  collections: CollectionCard,
};

export {
  CharacterCard,
  CollectionCard,
  FranchiseCard,
  HoverCard,
  InfluencerCard,
  ProductCard,
};
